import { useEffect, useState, useSyncExternalStore } from 'react';
import { AlertCircle, Download, ExternalLink, FileQuestion } from 'lucide-react';
import { Button } from './button';
import { Dialog, DialogSurface } from './Modal';
import { PdfViewer } from './PdfViewer';

/**
 * Datei-Vorschau für ERP und Partnerportal (ADR 0075, erweitert 2026-08-10).
 *
 * Anlass (Tech-Lead): Fast jede Datei landete bisher über „in neuem Tab öffnen"
 * als **Download** — technisch eine `blob:`-URL, die Safari für PDFs gar nicht
 * anzeigt und die in jedem Browser erst einmal im Download-Ordner liegt. Wer nur
 * kurz nachsehen will, welche Bestellung das war, hatte danach eine Datei mehr
 * auf der Platte und war aus der Liste heraus.
 *
 * Deshalb: **Anschauen ist der Normalfall, Herunterladen ein Knopf darin.**
 *
 * Bewusst ein Modul-Store statt eines React-Kontexts. Eine Vorschau ist immer
 * genau eine, sie gehört keinem Teilbaum, und Aufrufer sollen `openFilePreview()`
 * aus einem Zeilenmenü heraus aufrufen können, ohne dass jede Liste einen Provider
 * um sich braucht. Gerendert wird sie an genau einer Stelle: `<FilePreviewHost />`
 * in der App-Hülle.
 */

/** Was die Vorschau zum Laden und Herunterladen braucht — je App einmal gesetzt. */
export interface FilePreviewAdapter {
  /** Bytes zum Pfad holen (mit der Authentifizierung der App). */
  loadBytes: (path: string) => Promise<Uint8Array>;
  /** Datei herunterladen — Knopf im Kopf der Vorschau. */
  download: (path: string, filename: string) => Promise<void>;
  /** Fehlertext-Aufbereitung der App (z. B. `describeError`). */
  formatError?: (error: unknown) => string;
}

export interface FilePreviewRequest {
  /** API-Pfad der Datei, z. B. `/v1/sales-documents/:id/pdf`. */
  path: string;
  /** Kopfzeile, z. B. „Angebot AN-2610042". */
  title: string;
  /** Dateiname beim Herunterladen. Ohne Angabe der letzte Pfadteil. */
  filename?: string;
  /**
   * MIME-Typ, falls bekannt (Anhänge, DMS, Zeichnungen). Bestimmt, ob PDF-,
   * Bild- oder gar keine Vorschau gezeigt wird.
   */
  mimeType?: string;
  /** Optionaler Link „In neuem Tab" auf eine eigenständige Vorschau-Seite. */
  externalHref?: string;
}

export type FilePreviewKind = 'pdf' | 'image' | 'other';

const IMAGE_EXTENSIONS = ['png', 'jpg', 'jpeg', 'gif', 'webp', 'avif', 'bmp', 'svg'];

/** Dateiname aus dem Pfad (ohne Query), Fallback für den Download. */
export function filenameFromPath(path: string): string {
  const withoutQuery = path.split(/[?#]/, 1)[0] ?? path;
  const last = withoutQuery.split('/').filter(Boolean).pop();
  return last && last.length > 0 ? decodeURIComponent(last) : 'datei';
}

/**
 * Welche Vorschau passt?
 *
 * Ist der MIME-Typ bekannt, entscheidet er allein — ein `application/msword`
 * bekommt dann ehrlich „keine Vorschau" statt eines kaputten PDF-Versuchs.
 * Sonst zählt die Endung, und zwar die des **Dateinamens** vor der des Pfads:
 * Die Inhalts-Endpunkte heißen alle `…/content`, den echten Namen kennt nur
 * der Aufrufer. Bleibt am Ende gar keine Endung übrig (`…/report`,
 * `…/calculation/report?view=…`), ist es ein serverseitig erzeugtes PDF — das
 * sind alle Berichts-Endpunkte der API.
 */
export function detectFilePreviewKind(request: {
  path: string;
  filename?: string;
  mimeType?: string;
}): FilePreviewKind {
  const mime = request.mimeType?.toLowerCase().split(';', 1)[0]?.trim();
  if (mime) {
    if (mime === 'application/pdf') return 'pdf';
    if (mime.startsWith('image/')) return 'image';
    return 'other';
  }
  const name = (request.filename ?? filenameFromPath(request.path)).toLowerCase();
  const dot = name.lastIndexOf('.');
  if (dot < 0) return 'pdf';
  const extension = name.slice(dot + 1);
  if (extension === 'pdf') return 'pdf';
  if (IMAGE_EXTENSIONS.includes(extension)) return 'image';
  return 'other';
}

// ---------------------------------------------------------------------------
// Modul-Store
// ---------------------------------------------------------------------------

let adapter: FilePreviewAdapter | null = null;
let current: FilePreviewRequest | null = null;
const listeners = new Set<() => void>();

function emit(): void {
  for (const listener of listeners) listener();
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/** Einmal beim App-Start setzen (siehe `FilePreviewHost`). */
export function configureFilePreview(next: FilePreviewAdapter): void {
  adapter = next;
}

/** Vorschau öffnen. Ersetzt eine bereits offene Vorschau. */
export function openFilePreview(request: FilePreviewRequest): void {
  current = request;
  emit();
}

export function closeFilePreview(): void {
  if (current === null) return;
  current = null;
  emit();
}

function getSnapshot(): FilePreviewRequest | null {
  return current;
}

/**
 * Die gerade offene Vorschau — `null`, wenn keine offen ist.
 *
 * Für Tests: Eine Aufrufstelle prüft damit, **was** sie angemeldet hat, ohne
 * `@proerp/ui` mocken oder den ganzen Dialog rendern zu müssen. Im Produktivcode
 * hat niemand einen Grund, den Store direkt zu lesen — dafür ist
 * `FilePreviewHost` da.
 */
export function getFilePreviewRequest(): FilePreviewRequest | null {
  return current;
}

// ---------------------------------------------------------------------------
// Anzeige
// ---------------------------------------------------------------------------

/**
 * Einbau-Punkt der Vorschau — genau einmal in der App-Hülle rendern.
 * Solange nichts offen ist, kostet er nichts.
 */
export function FilePreviewHost() {
  const request = useSyncExternalStore(subscribe, getSnapshot, getSnapshot);

  return (
    <Dialog
      open={request !== null}
      onOpenChange={(open) => {
        if (!open) closeFilePreview();
      }}
    >
      {/* Nur rendern, wenn offen — sonst hinge dauerhaft ein Dialog-Baum in der App. */}
      {request && <FilePreviewDialog request={request} />}
    </Dialog>
  );
}

function FilePreviewDialog({ request }: { request: FilePreviewRequest }) {
  const [error, setError] = useState<string | null>(null);
  const filename = request.filename ?? filenameFromPath(request.path);
  const kind = detectFilePreviewKind(request);
  const describe = adapter?.formatError ?? ((cause: unknown) =>
    cause instanceof Error && cause.message ? cause.message : 'Unbekannter Fehler.');

  return (
    <DialogSurface
      title={request.title}
      description="Vorschau der Datei."
      visuallyHiddenTitle
      // Fast bildschirmfüllend: die meisten Belege sind hochkant, die Höhe zählt.
      className="h-[92dvh] max-h-none w-[min(1100px,94vw)] max-w-none"
    >
      {/* pr-12: rechts oben sitzt der Schließen-Knopf des Dialogs (absolut
          positioniert) — ohne die Reserve überdeckt er die Aktionen. */}
      <header className="flex shrink-0 flex-wrap items-center justify-between gap-3 border-b border-border py-2.5 pr-12 pl-4">
        <span className="truncate text-sm font-medium text-foreground">{request.title}</span>
        <div className="flex items-center gap-2">
          <Button
            variant="secondary"
            size="sm"
            onClick={() => {
              if (!adapter) {
                setError('Die Datei-Vorschau ist nicht eingerichtet.');
                return;
              }
              adapter.download(request.path, filename).catch((cause) => setError(describe(cause)));
            }}
          >
            <Download className="h-4 w-4" strokeWidth={1.5} /> Herunterladen
          </Button>
          {request.externalHref && (
            <Button variant="ghost" size="sm" asChild>
              <a href={request.externalHref} target="_blank" rel="noreferrer">
                <ExternalLink className="h-4 w-4" strokeWidth={1.5} /> In neuem Tab
              </a>
            </Button>
          )}
        </div>
      </header>

      <div className="min-h-0 flex-1 bg-muted/40">
        {error ? (
          <PreviewMessage icon={<AlertCircle className="h-6 w-6 text-danger-500" strokeWidth={1.5} aria-hidden />}>
            {error}
          </PreviewMessage>
        ) : !adapter ? (
          <PreviewMessage icon={<AlertCircle className="h-6 w-6 text-danger-500" strokeWidth={1.5} aria-hidden />}>
            Die Datei-Vorschau ist nicht eingerichtet.
          </PreviewMessage>
        ) : kind === 'pdf' ? (
          // Gezeichnet mit pdf.js, NICHT im iframe: Safari zeigt blob:-PDFs im
          // Rahmen nicht an (ADR 0075). Laden und Fehlerzustand liegen dort.
          <PdfViewer
            path={request.path}
            loadBytes={adapter.loadBytes}
            title={request.title}
            formatError={adapter.formatError}
          />
        ) : kind === 'image' ? (
          <ImagePreview
            path={request.path}
            title={request.title}
            mimeType={request.mimeType}
            loadBytes={adapter.loadBytes}
            formatError={describe}
          />
        ) : (
          <PreviewMessage icon={<FileQuestion className="h-6 w-6" strokeWidth={1.5} aria-hidden />}>
            Diese Datei lässt sich hier nicht anzeigen. Mit „Herunterladen" öffnest du sie im
            passenden Programm.
          </PreviewMessage>
        )}
      </div>
    </DialogSurface>
  );
}

function PreviewMessage({ icon, children }: { icon: React.ReactNode; children: React.ReactNode }) {
  return (
    <div className="flex h-full flex-col items-center justify-center gap-2 px-6 text-center text-sm text-muted-foreground">
      {icon}
      <span className="max-w-[42ch]">{children}</span>
    </div>
  );
}

/**
 * Bild-Vorschau. Anders als beim PDF darf hier eine `blob:`-URL entstehen: Das
 * CSP erlaubt `img-src … blob:`, und `<img>` führt nichts aus — auch bei SVG nicht.
 */
function ImagePreview({
  path,
  title,
  mimeType,
  loadBytes,
  formatError,
}: {
  path: string;
  title: string;
  mimeType?: string;
  loadBytes: (path: string) => Promise<Uint8Array>;
  formatError: (error: unknown) => string;
}) {
  const [url, setUrl] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let isCancelled = false;
    let created: string | null = null;
    loadBytes(path)
      .then((bytes) => {
        if (isCancelled) return;
        // Der Umweg über `buffer.slice`: `Uint8Array` deckt im Typsystem auch
        // einen `SharedArrayBuffer` ab, den `Blob` nicht annimmt — hier kommt
        // er immer aus `fetch().arrayBuffer()` und ist ein gewöhnlicher Puffer.
        const part = bytes.buffer.slice(
          bytes.byteOffset,
          bytes.byteOffset + bytes.byteLength,
        ) as ArrayBuffer;
        created = URL.createObjectURL(new Blob([part], mimeType ? { type: mimeType } : undefined));
        setUrl(created);
      })
      .catch((cause: unknown) => {
        if (!isCancelled) setError(`Bild konnte nicht geladen werden: ${formatError(cause)}`);
      });
    return () => {
      isCancelled = true;
      if (created) URL.revokeObjectURL(created);
    };
    // `loadBytes`/`formatError` sind je App feste Funktionen; der Pfad ist die Identität.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [path]);

  if (error) {
    return (
      <PreviewMessage icon={<AlertCircle className="h-6 w-6 text-danger-500" strokeWidth={1.5} aria-hidden />}>
        {error}
      </PreviewMessage>
    );
  }
  if (!url) {
    return <PreviewMessage icon={null}>Bild wird geladen …</PreviewMessage>;
  }
  return (
    <div className="flex h-full w-full items-center justify-center overflow-auto p-4">
      <img src={url} alt={title} className="max-h-full max-w-full object-contain" />
    </div>
  );
}
